import { useState } from "react";
import type React from "react";
import axios from "axios";
import logo from "../../assets/logo.svg";

const ForgotPassword: React.FC = () => {
  const [formData, setFormData] = useState({
    email: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({
    email: "",
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (!formData.email || !validateEmail(formData.email)) {
      setFormErrors({
        email: "Please enter a valid email",
      });
      return;
    }
    setLoading(true);
    setFormErrors({ email: "" });
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/api/v1/auth/forgot-password`,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
        data: {
          email: formData.email,
          role: 1,
        },
      });
      if (response.status === 200) {
        setSuccess(true);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Error resetting password");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setError("");
    setSuccess(false);
    setFormErrors({ email: "" });
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg border-t-4 border-brand/70 p-8">
          {/* Logo inside card */}
          <div className="flex flex-col items-center mb-6">
            <img
              src={logo}
              alt="Fincentive Logo"
              className="h-14 w-auto drop-shadow-md mt-4 mb-3"
              style={{ maxWidth: "220px" }}
            />
          </div>
          {loading && (
            <div className="flex flex-col items-center justify-center py-8">
              <svg className="w-8 h-8 text-green-600 animate-spin mb-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              <p className="text-gray-600">Sending reset link...</p>
            </div>
          )}
          {!loading && (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none transition-colors ${
                    formErrors.email ? "border-red-300" : "border-gray-300"
                  }`}
                />
                {formErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                )}
              </div>
              <button
                type="submit"
                className="w-full bg-green-600 font-medium text-white py-2 px-3 text-md rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
              >
                Send Link
              </button>
              <div className="text-center mt-4">
                <button
                  type="button"
                  className="text-sm text-green-600 hover:text-green-700 font-medium transition-colors"
                  onClick={() => {
                    window.location.href = "mailto:operations@fincentive.co?subject=Password Reset Support";
                  }}
                >
                  Need more help? Contact support.
                </button>
              </div>
            </form>
          )}
          {error && (
            <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                <span className="text-sm font-medium">{error}</span>
              </div>
            </div>
          )}
          {success && (
            <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                <span className="text-sm font-medium">Email Sent</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword; 